import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The two verification signals, always both shown, directly under the portrait.
 *
 * Fixed pair rather than a list of badges that appear when earned: "no identity
 * check on file" is information a patient should see, and a badge that is simply
 * absent says nothing — an empty row reads as "this card has no badges yet"
 * rather than "this provider is unverified".
 *
 * NPI  — the number is in the federal NPPES registry (links out to the record).
 * ID   — a human checked this person's government ID (providers.verified_badge).
 *
 * Claimed / premium deliberately live elsewhere: they say who pays us, not what
 * was verified, and a second check-mark icon beside these two read as a
 * duplicate of them.
 */
export function TrustBadges({
  npi,
  identityVerified,
  className,
}: {
  npi?: string | null;
  identityVerified?: boolean;
  className?: string;
}) {
  const trimmed = npi?.trim();
  const validNpi = trimmed && /^\d{10}$/.test(trimmed) ? trimmed : null;

  return (
    <div className={cn("flex items-center justify-center gap-2", className)}>
      <Badge
        ok={!!validNpi}
        label={
          validNpi
            ? `NPI ${validNpi} — open the NPPES registry`
            : "No NPI on file for this listing"
        }
        href={validNpi ? `https://npiregistry.cms.hhs.gov/provider-view/${validNpi}` : undefined}
      >
        <span className="text-[8px] font-bold leading-none tracking-[0.02em]">NPI</span>
      </Badge>
      <Badge
        ok={!!identityVerified}
        label={identityVerified ? "Identity verified" : "Identity not verified"}
      >
        <span className="text-[8px] font-bold leading-none tracking-[0.02em]">ID</span>
      </Badge>
    </div>
  );
}

/** Circle with the signal inside and its state as a corner marker. */
function Badge({
  ok,
  label,
  href,
  children,
}: {
  ok: boolean;
  label: string;
  href?: string;
  children: React.ReactNode;
}) {
  const circle = (
    <span
      className={cn(
        // Glass: translucent fill over a blur, one hairline ring, and a soft
        // shadow for lift. No solid background — the tint has to read against
        // both the card and the profile's gradient header.
        // Ring and label stay foreground in both states — colour is reserved
        // for the marker, so the pair reads as one consistent object and only
        // the corner says whether the signal is present.
        "flex h-6 w-6 items-center justify-center rounded-full bg-background/60 ring-1 ring-foreground/70 backdrop-blur-md",
        "shadow-[0_1px_2px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.6)]",
        ok ? "text-foreground" : "text-foreground/45 ring-foreground/30",
      )}
    >
      {children}
    </span>
  );

  return (
    // relative wrapper, not the circle itself: the corner marker must overflow
    // the circle's rounded edge rather than be clipped inside it.
    <span className="relative inline-flex" title={label} aria-label={label}>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex rounded-full transition-transform hover:scale-105"
        >
          {circle}
        </a>
      ) : (
        circle
      )}
      <span
        aria-hidden
        className={cn(
          // Brand blue for the earned state, not the green used elsewhere for
          // in-network: green here would introduce a second "good" colour on a
          // card that already spends green on insurance status.
          // Sits OUTSIDE the circle's arc, not on it: at this size a marker
          // tucked inside the edge lands on top of the centred NPI/ID text.
          "pointer-events-none absolute -bottom-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full ring-2 ring-background",
          ok ? "bg-primary text-primary-foreground" : "bg-muted-foreground/50 text-background",
        )}
      >
        {ok ? <Check className="h-1.5 w-1.5" strokeWidth={4} /> : <X className="h-1.5 w-1.5" strokeWidth={4} />}
      </span>
    </span>
  );
}
